import { Renderer } from '@unseenco/taxi';
import { isDEVMODE, viewStorage } from '../_globals'
import Accordions from '../modules/accordions'
import WknSwiper from '../modules/wkn-swiper'
import Form from '../modules/form'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
gsap.registerPlugin(ScrollTrigger)

export default class PageLandingDevisOffer extends Renderer {
  initialLoad() {
    const onDOMContentLoaded = () => {
      document.removeEventListener('DOMContentLoaded', onDOMContentLoaded)
      this.onEnter()
    }

    document.addEventListener('DOMContentLoaded', onDOMContentLoaded)
  }

  onEnter() {
    this.DOM = {
      testimonialsAlternativeSwiper: viewStorage.current.querySelector('.TestimonialsAlternative .swiper'),
      relatedQuestions: viewStorage.current.querySelector('.RelatedQuestions'),
      macaron: viewStorage.current.querySelector('.SimulatorDevis .SimulatorMacaron'),
      form: viewStorage.current.querySelector('.OfferForm'),
      formRow: viewStorage.current.querySelector('.OfferForm__row'),
      steps: viewStorage.current.querySelectorAll('.OfferForm__step'),
      nextBtn: viewStorage.current.querySelector('.OfferForm__next'),
      prevBtn: viewStorage.current.querySelector('.OfferForm__prev')
    }

    this.currentStep = 1

    this.init()
  }

  init() {
    if (this.DOM.form) {
      this.initForm()
    }

    if (this.DOM.testimonialsAlternativeSwiper) {
      this.testimonialsSwiperModule = new WknSwiper(this.DOM.testimonialsAlternativeSwiper)
    }

    if (this.DOM.relatedQuestions) {
      this.accordionsModule = new Accordions(this.DOM.relatedQuestions)
    }
  }

  initForm() {
    this.formModule = new Form(this.DOM.form)

    // Capture UTM parameters from URL
    this.captureUtmParams()

    // Initialize steps navigation
    this.initStepsNavigation()

    // Listen to formRow scroll to detect when reaching the end
    this.handleFormScroll = this.handleFormScroll.bind(this)
    this.DOM.formRow.addEventListener('scroll', this.handleFormScroll)
  }

  captureUtmParams() {
    const urlParams = new URLSearchParams(window.location.search)
    const utmSource = urlParams.get('utm_source')
    const utmCampaign = urlParams.get('utm_campaign')

    if (utmSource) {
      const utmSourceField = this.DOM.form.querySelector('.js-utm-source')
      if (utmSourceField) utmSourceField.value = utmSource
    }

    if (utmCampaign) {
      const utmCampaignField = this.DOM.form.querySelector('.js-utm-campaign')
      if (utmCampaignField) utmCampaignField.value = utmCampaign
    }
  }

  initStepsNavigation() {
    if (!this.DOM.nextBtn || !this.DOM.prevBtn) return

    // Bind methods
    this.handleNext = this.handleNext.bind(this)
    this.handlePrev = this.handlePrev.bind(this)
    this.validateStep1 = this.validateStep1.bind(this)

    // Event listeners for buttons
    this.DOM.nextBtn.addEventListener('click', this.handleNext)
    this.DOM.prevBtn.addEventListener('click', this.handlePrev)

    // Get all required fields from step 1
    const step1 = this.DOM.steps[0]
    this.step1Inputs = step1.querySelectorAll('input[required], select[required]')
    this.step1PrevoyanceRadios = step1.querySelectorAll('input[type="radio"][name="prevoyance"]')
    this.step1ParticipationRadios = step1.querySelectorAll('input[type="radio"][name="participation"]')

    // Listen to changes on step 1 fields
    this.step1Inputs.forEach(input => {
      input.addEventListener('input', this.validateStep1)
      input.addEventListener('change', this.validateStep1)
    })

    // Listen to prevoyance radios
    this.step1PrevoyanceRadios.forEach(radio => {
      radio.addEventListener('change', this.validateStep1)
    })

    // Listen to participation radios
    this.step1ParticipationRadios.forEach(radio => {
      radio.addEventListener('change', this.validateStep1)
    })

    // Initial validation
    this.validateStep1()
  }

  validateStep1() {
    let isValid = true

    // Check all required fields
    this.step1Inputs.forEach(input => {
      if (input.type !== 'radio' && !input.value.trim()) {
        isValid = false
      }
    })

    // Check prevoyance radios
    const prevoyanceChecked = Array.from(this.step1PrevoyanceRadios).some(radio => radio.checked)
    if (!prevoyanceChecked) {
      isValid = false
    }

    // Check participation radios
    const participationChecked = Array.from(this.step1ParticipationRadios).some(radio => radio.checked)
    if (!participationChecked) {
      isValid = false
    }

    // Enable/disable next button
    this.DOM.nextBtn.disabled = !isValid
  }

  handleNext() {
    if (this.currentStep === 1) {
      // Hide step 1
      this.DOM.steps[0].style.display = 'none'
      // Show step 2
      this.DOM.steps[1].style.display = 'block'
      // Update current step
      this.currentStep = 2
      this.DOM.form.setAttribute('data-current-step', '2')
      // Scroll to top of form (internal scroll)
      this.DOM.formRow.scrollTop = 0
      // Scroll page to form
      this.DOM.macaron.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  handlePrev() {
    if (this.currentStep === 2) {
      // Hide step 2
      this.DOM.steps[1].style.display = 'none'
      // Show step 1
      this.DOM.steps[0].style.display = 'block'
      // Update current step
      this.currentStep = 1
      this.DOM.form.setAttribute('data-current-step', '1')
      // Scroll to top of form (internal scroll)
      this.DOM.formRow.scrollTop = 0
      // Scroll page to form
      this.DOM.macaron.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  handleFormScroll() {
    const { scrollTop, scrollHeight, clientHeight } = this.DOM.formRow
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1 // -1 to tolerate rounding

    if (isAtBottom) {
      this.DOM.form.classList.add('is-scrolled-to-bottom')
    } else {
      this.DOM.form.classList.remove('is-scrolled-to-bottom')
    }
  }

  onLeaveCompleted() {
    if (this.formModule) {
      this.formModule.destroy()
    }

    // Clean up scroll event listener
    if (this.DOM.formRow && this.handleFormScroll) {
      this.DOM.formRow.removeEventListener('scroll', this.handleFormScroll)
    }

    // Clean up navigation event listeners
    if (this.DOM.nextBtn && this.handleNext) {
      this.DOM.nextBtn.removeEventListener('click', this.handleNext)
    }

    if (this.DOM.prevBtn && this.handlePrev) {
      this.DOM.prevBtn.removeEventListener('click', this.handlePrev)
    }

    // Clean up step 1 fields event listeners
    if (this.step1Inputs) {
      this.step1Inputs.forEach(input => {
        input.removeEventListener('input', this.validateStep1)
        input.removeEventListener('change', this.validateStep1)
      })
    }

    if (this.step1PrevoyanceRadios) {
      this.step1PrevoyanceRadios.forEach(radio => {
        radio.removeEventListener('change', this.validateStep1)
      })
    }

    if (this.step1ParticipationRadios) {
      this.step1ParticipationRadios.forEach(radio => {
        radio.removeEventListener('change', this.validateStep1)
      })
    }

    if (this.testimonialsSwiperModule) {
      this.testimonialsSwiperModule.destroy()
    }

    if (this.accordionsModule) {
      this.accordionsModule.destroy()
    }
  }
}
